// Self-service waitlist store (waitlist/{lowercased-email} docs). A waitlist doc grants ZERO
// app access — the allowlist stays the sole gate (see firestore.rules). This lib is the
// data-access AuthContext / PendingScreen / Admin call. Mirrors adminAllowlist.ts's lowercase
// convention so ids + the stored email field match the rules' email.lower() gate.
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, onSnapshot, query, where, orderBy,
  type Unsubscribe,
} from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "./firebase";
import { addToAllowlist } from "./adminAllowlist";

export type WaitlistStatus = "pending" | "approved" | "declined";

export interface WaitlistEntry {
  email: string;
  uid: string | null;
  displayName: string | null;
  requestedAt: string | null;
  status: WaitlistStatus;
  approvedAt?: string | null;
  declinedAt?: string | null;
}

function toEntry(id: string, d: Partial<WaitlistEntry>): WaitlistEntry {
  return {
    email: d.email ?? id,
    uid: d.uid ?? null,
    displayName: d.displayName ?? null,
    requestedAt: d.requestedAt ?? null,
    status: (d.status as WaitlistStatus) ?? "pending",
    approvedAt: d.approvedAt ?? null,
    declinedAt: d.declinedAt ?? null,
  };
}

/** Enqueue a verified-but-not-allowlisted user. Idempotent + single-notify: getDoc first,
 *  and if a doc exists in ANY status (incl. declined/approved tombstones) return without a
 *  write — so re-signing-in never re-pings ken. Lowercases the email for BOTH the doc id and
 *  the stored `email` field (else the create rule's `data.email == email` check rejects it).
 *  Fire-and-forget from the caller. */
export async function joinWaitlist(user: User): Promise<void> {
  const email = user.email?.toLowerCase();
  if (!email) return;
  const ref = doc(db, "waitlist", email);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await setDoc(ref, {
    email,
    uid: user.uid ?? null,
    displayName: user.displayName ?? null,
    requestedAt: new Date().toISOString(),
    status: "pending",
  });
}

/** Live own-doc subscription (rules allow the owner `get`). PendingScreen uses this so the
 *  "saving…" → "on the list ✓" flip fires exactly when the create commits (a one-shot getDoc
 *  could race ahead of the fire-and-forget write). */
export function watchWaitlist(
  email: string,
  cb: (entry: WaitlistEntry | null) => void,
  onError?: (e: unknown) => void,
): Unsubscribe {
  const ref = doc(db, "waitlist", email.toLowerCase());
  return onSnapshot(
    ref,
    (snap) => cb(snap.exists() ? toEntry(snap.id, snap.data() as Partial<WaitlistEntry>) : null),
    (err) => { onError?.(err); },
  );
}

/** Admin read: all pending requests, oldest first. Needs the (status,requestedAt) composite
 *  index (firestore.indexes.json) — else this throws failed-precondition/needs-index. */
export async function listPendingWaitlist(): Promise<WaitlistEntry[]> {
  const q = query(collection(db, "waitlist"), where("status", "==", "pending"), orderBy("requestedAt"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => toEntry(d.id, d.data() as Partial<WaitlistEntry>));
}

/** Admin: grant access THEN tombstone approved (the approved tombstone fires the requester
 *  "you're in" email in Phase 2). */
export async function approveWaitlist(email: string): Promise<void> {
  const normalized = email.toLowerCase();
  await addToAllowlist(normalized);
  await updateDoc(doc(db, "waitlist", normalized), { status: "approved", approvedAt: new Date().toISOString() });
}

/** Admin: soft-decline tombstone — suppresses re-notify, sends no email, reversible. */
export async function dismissWaitlist(email: string): Promise<void> {
  await updateDoc(doc(db, "waitlist", email.toLowerCase()), { status: "declined", declinedAt: new Date().toISOString() });
}

/** Admin undo: re-list a dismissed request. */
export async function undismissWaitlist(email: string): Promise<void> {
  await updateDoc(doc(db, "waitlist", email.toLowerCase()), { status: "pending" });
}
